// Based on the Dawn version of related products, js reference: https://github.com/Shopify/dawn/blob/main/assets/global.js

const fetchSection = (url) => fetch(url, { credentials: 'same-origin' })
  .then((response) => (response.ok ? response.text() : ''))
  .then((html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null))
  .catch(() => null);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') this.ready();
    else this.load();
  }

  async load() {
    const doc = await fetchSection(this.dataset.url);
    const fetched = doc?.getElementById(this.id);
    if (fetched) this.innerHTML = fetched.innerHTML;
    this.ready();
  }

  async hydrate(handles) {
    const { sectionId, productHandle, limit } = this.dataset;
    const list = this.querySelector('[data-list]');
    const unique = [...new Set(handles)].filter((handle) => handle && handle !== productHandle).slice(0, Number(limit));
    const docs = await Promise.all(unique.map((handle) => fetchSection(`/products/${handle}?section_id=${sectionId}`)));
    docs.forEach((doc) => {
      const slide = doc?.querySelector('template[data-slide]')?.content;
      if (slide) list.append(slide.cloneNode(true));
    });
  }

  ready() {
    if (!this.querySelector('.product-card')) {
      this.closest('.shopify-section').hidden = true;
      return;
    }
    this.classList.remove('is-loading');
    if (this.dataset.layout === 'grid') this.readyGrid();
    else this.readyCarousel();
  }

  readyGrid() {
    const items = this.querySelectorAll('[data-list] > *');
    const visible = Number(this.dataset.visibleMobile) || 4;
    items.forEach((item, index) => item.classList.toggle('is-hidden-mobile', index >= visible));
    const more = this.querySelector('details');
    if (more) more.hidden = items.length <= visible;
  }

  readyCarousel() {
    const { columnsMobile = 2, columnsDesktop = 4, arrows = 'true' } = this.dataset;
    new Splide(this.querySelector('.splide'), {
      type: 'slide',
      perPage: Number(columnsMobile),
      perMove: 1,
      gap: '1rem',
      padding: { right: '0.625rem' },
      arrows: false,
      pagination: false,
      mediaQuery: 'min',
      breakpoints: {
        769: { perPage: Math.min(Number(columnsDesktop), 3), padding: 0 },
        1025: { perPage: Number(columnsDesktop), gap: '1.5625rem', padding: 0, arrows: arrows === 'true' },
      },
    }).mount();
  }
}

class RebuyRecommendations extends ProductRecommendations {
  static visitorId() {
    const raw = document.cookie.match(/(?:^|; )_ruid=([^;]+)/)?.[1];
    try {
      return raw ? JSON.parse(atob(decodeURIComponent(raw))).uuid : null;
    } catch {
      return null;
    }
  }

  async load() {
    const { rebuyId, rebuyKey, productId, limit } = this.dataset;
    const params = new URLSearchParams({ key: rebuyKey, limit, shopify_product_ids: productId });
    const uuid = RebuyRecommendations.visitorId();
    if (uuid) params.set('uuid', uuid);
    const response = await fetch(`https://rebuyengine.com/api/v1/custom/id/${rebuyId}?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    await this.hydrate((response?.data ?? []).map((item) => item.handle));
    this.ready();
  }
}

class RecentlyViewedProducts extends ProductRecommendations {
  static storageKey = 'alc:recently-viewed';

  static read() {
    try {
      return JSON.parse(localStorage.getItem(RecentlyViewedProducts.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  async load() {
    const { productHandle, max } = this.dataset;
    await this.hydrate(RecentlyViewedProducts.read());
    this.ready();
    try {
      const history = [productHandle, ...RecentlyViewedProducts.read().filter((handle) => handle !== productHandle)];
      localStorage.setItem(RecentlyViewedProducts.storageKey, JSON.stringify(history.slice(0, Number(max) || 12)));
    } catch {
      /* storage unavailable */
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);
customElements.define('rebuy-recommendations', RebuyRecommendations);
customElements.define('recently-viewed-products', RecentlyViewedProducts);
